import { DatePicker, Form, Input, notification, Tooltip } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useAuth } from '../../../../security/authContext';
import apiATS from '../../../../services/apiATS';
import apiNetCore from '../../../../services/apiNetCore';
import { getIdCidades } from '../helpers/cidades';
import { numberElement, selectElement } from '../helpers/input';
import { EditableContext } from './editableRow';
import { EditableCol, TableCol } from './styles';

export function EditableCell({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  errors,
  required,
  inputType,
  tipoCliente,
  ...restProps
}) {
  let childNode = children;
  const { user } = useAuth();
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  const [editing, setEditing] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const temDadosEndereco = ({ idCidadeOrigem, idCidadeDestino }) =>
    idCidadeOrigem === undefined || idCidadeDestino === undefined;

  const toggleEdit = () => {
    if (
      dataIndex !== 'cnpjClientePagador' &&
      record.errors.cnpjClientePagador
    ) {
      return notification.error({
        message: record.errors.cnpjClientePagador,
        description: 'Por favor, selecione um pagador válido para continuar.',
      });
    }

    if (dataIndex.includes('frete')) {
      if (temDadosEndereco(getIdCidades(record))) {
        notification.error({
          message: 'Nenhuma cidade selecionada',
          description: 'Selecione pelo menos a cidade de origem e destino',
        });
        return;
      }
    }

    if (dataIndex === 'especie' && record.clienteTemProduto) {
      return;
    }

    setEditing(!editing);
  };

  const titleTooltip = errors
    ? `${errors} - Clique para editar`
    : 'Clique para editar';

  const save = async () => {
    try {
      const values = await form.validateFields();

      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.warn('Save failed:', errInfo);
    }
  };

  const searchCidades = async value => {
    setLoadingData(true);
    const response = await apiATS.get('/CidadeAts/GetCidadeCotacao', {
      params: { cidadeNome: value },
    });

    record[`cidade${tipoCliente}Data`] = response.data.map(cidade => ({
      idCidade: cidade.idCidade,
      cidade: cidade.cidadeNome,
      idEstado: cidade.idEstado,
      uf: cidade.sigla,
    }));
    setLoadingData(false);
  };

  const onSearchClientes = useDebouncedCallback(async value => {
    if (!value || value.length < 3) return;
    setLoadingData(true);

    const { data } = await apiNetCore.get('/ClienteAts/GetClientes', {
      params: {
        clienteParametro: value,
      },
    });

    record[`dataCliente${tipoCliente}`] = data;
    setLoadingData(false);
  }, 1000);

  const onSearchCidades = useDebouncedCallback(async value => {
    if (!value || value.length < 3) return;
    await searchCidades(value);
  }, 1000);

  const onSearchProdutos = useDebouncedCallback(async value => {
    if (!value || value.length < 3) return;
    setLoadingData(true);

    const { data } = await apiNetCore.get(
      '/ProdutoAts/ConsultarProdutosDescricao',
      {
        params: { descricao: value },
      }
    );
    const produtos = data.data.map(({ IdProduto, Descricao }) => ({
      codigoProduto: IdProduto,
      descricaoProduto: Descricao,
    }));

    record.dataProdutos = produtos;
    setLoadingData(false);
  }, 1000);

  const getInput = () => {
    const dateFormat = 'DD/MM/YYYY';
    let initialValue = record[dataIndex];
    let inputElement = '';

    switch (inputType) {
      case 'number':
        inputElement = numberElement(record, dataIndex, form, save, inputRef);
        break;
      case 'date':
        initialValue = record[dataIndex]
          ? moment(record[dataIndex], 'YYYY-MM-DD')
          : '';
        inputElement = (
          <DatePicker
            className="total-width"
            ref={inputRef}
            onPressEnter={save}
            onChange={save}
            onBlur={save}
            format={dateFormat}
            showToday={false}
            disabledDate={d => !d || d.isBefore(record.dataInicio)}
            {...(dataIndex === 'dataInicio' && {
              disabledDate: d => !d || d.isBefore(moment().add(-1, 'days')),
              allowClear: false,
            })}
          />
        );
        break;
      case 'select':
        inputElement = selectElement(
          user,
          form,
          record,
          inputRef,
          save,
          dataIndex,
          tipoCliente,
          loadingData,
          setLoadingData,
          searchCidades,
          onSearchCidades,
          onSearchProdutos,
          onSearchClientes,
          required
        );
        break;
      default:
        inputElement = (
          <Input ref={inputRef} onPressEnter={save} onBlur={save} />
        );
    }

    return (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        initialValue={initialValue}
      >
        {inputElement}
      </Form.Item>
    );
  };

  if (editable) {
    if (editing) {
      childNode = getInput();
    } else {
      return (
        <Tooltip placement="topRight" title={titleTooltip}>
          <TableCol
            {...restProps}
            editable={true}
            onClick={toggleEdit}
            errors={errors}
          >
            <EditableCol errors={errors}>{children}</EditableCol>
          </TableCol>
        </Tooltip>
      );
    }
  }

  return <TableCol {...restProps}>{childNode}</TableCol>;
}

EditableCell.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  editable: PropTypes.bool,
  children: PropTypes.arrayOf(PropTypes.any),
  dataIndex: PropTypes.string,
  record: PropTypes.objectOf(PropTypes.any),
  handleSave: PropTypes.func,
  errors: PropTypes.string,
  required: PropTypes.bool,
  inputType: PropTypes.string,
  tipoCliente: PropTypes.string,
};

EditableCell.defaultProps = {
  title: '',
  editable: false,
  children: {},
  dataIndex: '0',
  record: {},
  handleSave: () => {},
  errors: '',
  required: false,
  inputType: '',
  tipoCliente: '',
};
